import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

interface VersionResponse {
  version: string;
}

@Injectable({
  providedIn: 'root',
})
export class VersioncheckService implements OnDestroy {

  // ─── Config ────────────────────────────────────────────────────────────────

  private readonly VERSION_URL = '/assets/version/version.json';
  private readonly CHECK_INTERVAL_MS = 3 * 60 * 1000;
  private readonly MAX_BACKOFF_MS = 30 * 60 * 1000;
  private readonly SESSION_KEY = 'vcs_reload';

  // ─── State ─────────────────────────────────────────────────────────────────

  private currentVersion: string | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private backoffMs = this.CHECK_INTERVAL_MS;
  private checkInFlight = false;

  readonly updateAvailable$ = new BehaviorSubject<boolean>(false);

  // ─── Visibility handling ────────────────────────────────────────────────────

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.checkForUpdate();
    }
  };

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  constructor(private readonly http: HttpClient) {}

  ngOnDestroy(): void {
    this.destroyVersionCheck();
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  startVersionCheck(): void {
    if (this.intervalId !== null) {
      return;
    }

    // ✅ Do NOT wipe sessionStorage here.
    // The guard must survive the reload that this service triggers.
    // It is only cleared once the live version has moved past the guarded version
    // (handled inside checkForUpdate on the first successful response).

    this.scheduleInterval();
    this.checkForUpdate();

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  destroyVersionCheck(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private scheduleInterval(): void {
    this.intervalId = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        this.checkForUpdate();
      }
    }, this.backoffMs);
  }

  private rescheduleWithBackoff(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.backoffMs = Math.min(this.backoffMs * 2, this.MAX_BACKOFF_MS);
    this.scheduleInterval();
  }

  private resetBackoff(): void {
    if (this.backoffMs !== this.CHECK_INTERVAL_MS) {
      this.backoffMs = this.CHECK_INTERVAL_MS;
      if (this.intervalId !== null) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      this.scheduleInterval();
    }
  }

  private checkForUpdate(): void {
    if (this.checkInFlight) {
      return;
    }

    this.checkInFlight = true;

    const url = `${this.VERSION_URL}?t=${Date.now()}`;

    this.http.get<VersionResponse>(url).subscribe({

      next: (data) => {
        this.checkInFlight = false;
        this.resetBackoff();

        const incoming = data?.version?.trim();

        if (!incoming) {
          return;
        }

        if (!this.currentVersion) {
          // First fetch after boot (or after a reload).
          this.currentVersion = incoming;

          // ✅ Safe to clear the guard now: the app has booted cleanly on
          // `incoming`. If a future check detects yet another version, the
          // guard will be re-set with that newer version string — no loop.
          const guarded = sessionStorage.getItem(this.SESSION_KEY);
          if (guarded && guarded === incoming) {
            sessionStorage.removeItem(this.SESSION_KEY);
          }

          return;
        }

        if (this.currentVersion !== incoming) {
          this.handleUpdateDetected(incoming);
        }
      },

      error: (err) => {
        this.checkInFlight = false;
        this.rescheduleWithBackoff();
      },
    });
  }

  private handleUpdateDetected(newVersion: string): void {
    this.updateAvailable$.next(true);

    // Guard: already reloaded for this exact version — do not loop.
    if (sessionStorage.getItem(this.SESSION_KEY) === newVersion) {
      return;
    }

    sessionStorage.setItem(this.SESSION_KEY, newVersion);
    this.currentVersion = newVersion;

    window.location.reload();
  }
}